import React, { useEffect, useMemo, useRef, useState } from 'react';
import { upvoteEvent, hasUserUpvoted } from '../services/api';
import { useEvents } from '../hooks/useEvents';
import { useFavorites } from '../hooks/useFavorites';
import { useToast } from '../context/ToastContext';
import useMetaTags from '../hooks/useMetaTags';
import EventFilters from '../components/EventFilters';
import EventList from '../components/EventList';
import SkeletonCard from '../components/SkeletonCard';
import { filterEvents, sortEvents, SportFilter, StatusFilter } from '../utils/eventUtils';
import styles from './Events.module.css';

const Events: React.FC = () => {
    const { events, loading, refreshing, error, refresh, updateEvent } = useEvents(true);
    const { favoriteIds, toggleFavorite, isFavorite, favoriteCount } = useFavorites();
    const { showToast } = useToast();

    const [sportTab, setSportTab] = useState<SportFilter>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [favoritesOnly, setFavoritesOnly] = useState(false);
    const defaultFilterApplied = useRef(false);

    useMetaTags({
        title: 'সব লাইভ ম্যাচ | BDLINK LIVE',
        description: 'সব ক্রিকেট ও ফুটবল লাইভ ম্যাচ এক জায়গায় দেখুন।',
        url: 'https://bdlink.live/events',
        canonicalUrl: 'https://bdlink.live/events',
        locale: 'bn_BD',
        robots: 'index, follow',
    });

    const liveCount = events.filter((e) => e.status === 'live').length;
    const cricketCount = events.filter((e) => e.category === 'Cricket').length;
    const footballCount = events.filter((e) => e.category === 'Football').length;

    useEffect(() => {
        if (defaultFilterApplied.current || loading) return;
        if (events.length === 0) return;
        defaultFilterApplied.current = true;
        if (liveCount > 0) setStatusFilter('live');
    }, [loading, events.length, liveCount]);

    const filteredEvents = useMemo(
        () =>
            sortEvents(
                filterEvents(events, {
                    sportTab,
                    statusFilter,
                    searchQuery,
                    favoritesOnly,
                    favoriteIds,
                })
            ),
        [events, sportTab, statusFilter, searchQuery, favoritesOnly, favoriteIds]
    );

    const handleUpvote = (e: React.MouseEvent, eventId: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (hasUserUpvoted(eventId)) {
            showToast('ইতিমধ্যে আপভোট করা হয়েছে', 'info');
            return;
        }
        const newUpvotes = upvoteEvent(eventId);
        updateEvent(eventId, { upvotes: newUpvotes });
        showToast('আপভোট যোগ হয়েছে', 'success');
    };

    const handleFavorite = (e: React.MouseEvent, eventId: string) => {
        e.preventDefault();
        e.stopPropagation();
        const wasFavorite = isFavorite(eventId);
        toggleFavorite(eventId);
        showToast(wasFavorite ? 'পছন্দ থেকে সরানো হয়েছে' : 'পছন্দে যোগ হয়েছে', 'success');
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <span className={styles.eyebrow}>BDLINK LIVE</span>
                <h1>সব ম্যাচ</h1>
                <p>খুঁজুন, ফিল্টার করুন, সরাসরি দেখুন</p>
            </header>

            {error && (
                <div className={styles.error} role="alert">
                    {error}
                </div>
            )}

            <div className={styles.container}>
                <div className={styles.stickyFilters}>
                    <EventFilters
                        sportTab={sportTab}
                        statusFilter={statusFilter}
                        searchQuery={searchQuery}
                        favoritesOnly={favoritesOnly}
                        favoriteCount={favoriteCount}
                        resultCount={filteredEvents.length}
                        cricketCount={cricketCount}
                        footballCount={footballCount}
                        onSportChange={setSportTab}
                        onStatusChange={setStatusFilter}
                        onSearchChange={setSearchQuery}
                        onFavoritesToggle={() => setFavoritesOnly((v) => !v)}
                        onRefresh={refresh}
                        refreshing={refreshing}
                    />
                </div>

                {loading ? (
                    <div className={styles.list}>
                        {Array.from({ length: 10 }).map((_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </div>
                ) : filteredEvents.length === 0 ? (
                    <div className={styles.empty}>
                        <p>কোন ম্যাচ পাওয়া যায়নি</p>
                    </div>
                ) : (
                    <EventList
                        events={filteredEvents}
                        isFavorite={isFavorite}
                        hasUpvoted={hasUserUpvoted}
                        onUpvote={handleUpvote}
                        onToggleFavorite={handleFavorite}
                        groupByStatus={statusFilter === 'all'}
                        hideCompletedDefault={statusFilter === 'all'}
                    />
                )}
            </div>
        </div>
    );
};

export default Events;
